<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>

    <b-card>
      <div class="d-flex mb-4 mb-xl-2 justify-content-between align-items-center">
        <h4 class="mb-0 font-weight-bold">預約設定檔管理</h4>
        <div class="d-flex align-items-center">
          <b-button
            v-if="checkPermission([consts.BOOKING_PRESET_V2_CREATE])"
            class="flex-shrink-0 mr-3"
            variant="primary"
            :to="{ name: 'BookingPresetCreateMinimal' }"
          >
            <i class="fa fa-plus"></i>新增預約設定檔 (極簡介面)
          </b-button>
          <b-button
            v-if="checkPermission([consts.BOOKING_PRESET_CREATE])"
            class="flex-shrink-0 mr-3"
            variant="primary"
            :to="{ name: 'BookingPresetCreate' }"
          >
            <i class="fa fa-plus"></i>新增預約設定檔
          </b-button>
          <b-form-select
            v-model="query.is_valid"
            :options="validityStatusOptions"
            class="mr-3"
            @change="handlePageReset"
          />
          <b-form-select
            v-model="query.status"
            :options="publishStatusOptions"
            class="mr-3"
            @change="handlePageReset"
          />
          <b-input-group style="min-width: 300px;">
            <b-form-input
              v-model="searchKeyword"
              placeholder="搜尋"
              @keydown.enter="handleSearchKeyPress"
              @compositionstart="isComposing = true"
              @compositionend="isComposing = false"
            ></b-form-input>
            <b-input-group-append>
              <b-button @click="handleSearch">
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="bookingPresets"
                :fields="fields"
              >
                <template #cell(is_valid)="data">
                  <b-badge
                    :variant="data.item.is_valid ? 'success' : 'danger'"
                  >
                    {{ data.item.is_valid ? '生效中' : '已失效' }}
                  </b-badge>
                </template>
                <template #cell(status)="data">
                  <b-badge
                    v-if="data.item.status == 'published'"
                    variant="success"
                  >
                    發布
                  </b-badge>
                  <b-badge v-if="data.item.status == 'closed'" variant="danger">
                    關閉
                  </b-badge>
                  <b-badge
                    v-if="data.item.status == 'draft'"
                    variant="warning"
                  >
                    草稿
                  </b-badge>
                </template>
                <template #cell(actions)="data">
                  <b-dropdown
                    class="mr-1"
                    variant="secondary"
                  >
                    <template #button-content>
                      <i class="mdi mdi-menu"></i>
                    </template>
                    <b-dropdown-item
                      v-if="checkPermission([consts.BOOKING_PRESET_REPORT_EDIT])"
                      :to="{
                        name: 'BookingPresetReportForm',
                        params: { id: data.item.id },
                      }"
                    >
                      報表設定
                    </b-dropdown-item>
                  </b-dropdown>
                  <b-button
                    class="mr-1"
                    variant="inverse-dark"
                    @click="showFormUrl(data.item)"
                  >
                    <i class="fa fa-link"></i>
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_PRESET_CREATE])"
                    variant="inverse-info"
                    @click="replicateBookingPreset(data.item)"
                  >
                    <span class="mdi mdi-content-copy"></span> 複製
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_PRESET_V2_EDIT])"
                    variant="inverse-warning"
                    :to="{
                      name: 'BookingPresetEditMinimal',
                      params: { id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-lead-pencil"></span> 編輯 (極簡介面)
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BOOKING_PRESET_EDIT])"
                    variant="inverse-warning"
                    :to="{
                      name: 'BookingPresetEdit',
                      params: { id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-lead-pencil"></span> 編輯
                  </b-button>
                  <span
                    v-if="checkPermission([consts.BOOKING_PRESET_DELETE])"
                    class="d-inline-block"
                    v-b-tooltip.hover
                    :title="data.item.status !== 'closed' ? '發布狀態為關閉時，才能刪除' : ''"
                  >
                    <b-button
                      class="mr-1"
                      variant="inverse-danger"
                      :disabled="data.item.status !== 'closed'"
                      @click="data.item.status === 'closed' ? deleteBookingPreset(data.item) : null"
                    >
                      <span class="mdi mdi-delete"></span> 刪除
                    </b-button>
                  </span>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>

    <FormUrlModal ref="formUrlModal" />
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import bookingApi from "@/apis/booking";
import { mapState } from "vuex";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import FormUrlModal from "@/components/Dashboard/Booking/FormUrlModal.vue";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, FormUrlModal, BreadCrumb },
  mixins: [paginationMixin],
  data() {
    return {
      consts: consts,
      bookingPresets: [],
      showLoading: false,
      searchKeyword: "",
      isComposing: false,
      validityStatusOptions: [
        { value: null, text: '選擇生效狀態' },
        { value: true, text: '生效中' },
        { value: false, text: '已失效' }
      ],
      publishStatusOptions: [
        { value: null, text: '選擇發布狀態' },
        { value: 'published', text: '發布' },
        { value: 'draft', text: '草稿' },
        { value: 'closed', text: '關閉' }
      ],
      fields: [
        { key: "name", label: "預約名稱", sortable: true },
        { key: "is_valid", label: "生效狀態", sortable: false },
        { key: "status", label: "發布狀態", sortable: false },
        { key: "bookings_count", label: "目前預約筆數", sortable: true },
        {
          key: "created_at",
          label: "建立時間",
          sortable: true,
          formatter: (value) => {
            return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
          },
        },
        { key: "actions", label: "管理" },
      ],
      totalRows: 0,
      initialized: false,
      query: {
        keyword: "",
        per_page: 20,
        page: 1,
        is_valid: null,
        status: null,
        sort_by: "created_at",
        order_by: "desc",
      },
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}-${this.query.keyword}-${this.query.is_valid}-${this.query.status}-${this.query.sort_by}-${this.query.order_by}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = updateQueryFromRoute(to, this.$store, this.query);
      this.searchKeyword = this.query.keyword || "";
      this.fetchBookingPresets();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    this.searchKeyword = this.query.keyword || "";
  },
  async mounted() {
    await this.fetchBookingPresets();
    this.initialized = true;
  },
  methods: {
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchBookingPresets() {
      try {
        this.showLoading = true;
        let params = { ...this.query };
        if (params.is_valid === null) {
          delete params.is_valid;
        }
        if (params.status === null) {
          delete params.status;
        }
        let response = await bookingApi.getBookingPresets(params);
        this.bookingPresets = response.data.data;
        this.totalRows = response.data.meta.total;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取列表失敗", "error");
      } finally {
        this.showLoading = false;
      }
    },
    async deleteBookingPreset(bookingPreset) {
      try {
        this.showLoading = true;
        const result = await this.$swal({
          type: "warning",
          title: "您確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>刪除預約設定檔：${bookingPreset.name}</div>
              </div>
            </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        });

        if (!result.value) {
          return;
        }

        await bookingApi.deleteBookingPreset(bookingPreset.id);
        this.$swal("刪除成功", "", "success");
        await this.fetchBookingPresets();
      } catch (error) {
        console.error(error);
        this.$swal("刪除失敗", "", "error");
      } finally {
        this.showLoading = false;
      }
    },
    async replicateBookingPreset(bookingPreset) {
      try {
        const result = await this.$swal({
          type: "question",
          title: "複製預約設定檔",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>請問是否將「${bookingPreset.name}」複製成為新的預約設定？</div>
              </div>
            </div>`,
          confirmButtonText: "確認複製",
          cancelButtonText: "取消",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#6c757d",
          reverseButtons: true,
        });

        if (!result.value) {
          return;
        }

        this.showLoading = true;
        await bookingApi.replicateBookingPreset(bookingPreset.id);
        this.$swal("複製成功", "已成功複製預約設定檔", "success");
        await this.fetchBookingPresets();
      } catch (error) {
        console.error(error);
        this.$swal("複製失敗", "複製預約設定檔時發生錯誤", "error");
      } finally {
        this.showLoading = false;
      }
    },
    showFormUrl(bookingPreset) {
      this.$refs.formUrlModal.showFormUrl(bookingPreset);
    },
    handleSearchKeyPress(event) {
      if (this.isComposing) {
        event.preventDefault();
      } else {
        this.handleSearch();
      }
    },
    handleSearch() {
      this.query.keyword = this.searchKeyword;
      this.handlePageReset();
    },
  },
};
</script>
